import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardService, type DashboardStats, type QualityBreakdown } from '@/services/dashboard-service';
import {
  Header,
  ContentContainer,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Empty,
  Spinner,
  Badge,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from 'prosci-ui';
import { MemorySection } from './memory-section';
import { usePageContext } from '@/contexts/page-context';
import { GuidedUseCasesSection, type GuidedUseCaseStats } from './guided-use-cases-section';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend, BarChart, Bar,
} from 'recharts';

const COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe', '#f5f3ff'];
const QUALITY_COLORS = { positive: '#22c55e', negative: '#ef4444', none: '#e5e7eb' };
const TREND_COLORS = { conversations: '#6366f1', unique_users: '#8b5cf6', avg_messages: '#f59e0b' };

function formatChange(current: number, previous: number): { text: string; positive: boolean } {
  if (previous === 0) return { text: 'New', positive: true };
  const pct = Math.round(((current - previous) / previous) * 100);
  return { text: `${pct >= 0 ? '+' : ''}${pct}%`, positive: pct >= 0 };
}

function StatCard({ label, value, subtext, change }: {
  label: string;
  value: string | number;
  subtext: string;
  change?: { text: string; positive: boolean } | null;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-baseline gap-2">
          <div className="text-2xl font-bold">{value}</div>
          {change && (
            <Badge variant="outline" className={`text-[10px] ${change.positive ? 'text-green-600 border-green-300' : 'text-red-600 border-red-300'}`}>
              {change.text}
            </Badge>
          )}
        </div>
        <div className="text-sm font-medium text-muted-foreground mt-1">{label}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{subtext}</div>
      </CardContent>
    </Card>
  );
}

function QualityDonut({ data }: { data: QualityBreakdown }) {
  const total = data.total_feedback;
  const pct = total > 0 ? Math.round((data.positive_count / total) * 100) : 0;
  const chartData = total > 0
    ? [
        { name: 'Positive', value: data.positive_count },
        { name: 'Negative', value: data.negative_count },
      ]
    : [{ name: 'No feedback', value: 1 }];

  const colors = total > 0
    ? [QUALITY_COLORS.positive, QUALITY_COLORS.negative]
    : [QUALITY_COLORS.none];

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              innerRadius={35}
              outerRadius={55}
              dataKey="value"
              stroke="none"
            >
              {chartData.map((_, i) => (
                <Cell key={i} fill={colors[i % colors.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold">{total > 0 ? `${pct}%` : 'N/A'}</span>
        </div>
      </div>
      <div className="text-xs text-muted-foreground mt-1">{total} ratings</div>
    </div>
  );
}

export function KaiyaDashboardPage() {
  const { setShowPageHeader } = usePageContext();

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  const { data: stats, isLoading } = useQuery({
    queryKey: ['llmopsDashboard'],
    queryFn: () => dashboardService.getStats(),
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <>
        <Header title="Kaiya Dashboard" description="Overview of Kaiya AI system metrics" />
        <ContentContainer><Empty><Spinner /></Empty></ContentContainer>
      </>
    );
  }

  const s = stats as DashboardStats | undefined;
  if (!s) return null;

  const usersChange = formatChange(s.overview.unique_users_this_month, s.overview.unique_users_last_month);
  const convsChange = formatChange(s.overview.conversations_this_month, s.overview.conversations_last_month);

  // Prepare skill usage for pie chart
  const skillUsageData = s.skill_usage.map((entry) => ({
    name: entry.skill_name.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    value: entry.count,
  }));

  // Prepare execution mode & type for pie charts
  const executionModeData = Object.entries(s.skills.by_execution_mode)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v }));

  const skillTypeData = Object.entries(s.skills.by_type)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: k.charAt(0).toUpperCase() + k.slice(1), value: v }));

  return (
    <>
      <Header title="Kaiya Dashboard" description="Overview of Kaiya AI system metrics" />

      <ContentContainer>
        <Tabs defaultValue="overview">
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="memory">Memory</TabsTrigger>
          </TabsList>

          <TabsContent value="memory">
            <MemorySection />
          </TabsContent>

          <TabsContent value="overview">
        {/* Row 1: Overview Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Accounts"
            value={s.overview.total_accounts}
            subtext="total organizations"
          />
          <StatCard
            label="Unique Users"
            value={s.overview.unique_users_this_month}
            subtext="active this month"
            change={usersChange}
          />
          <StatCard
            label="Conversations"
            value={s.overview.conversations_this_month}
            subtext="this month"
            change={convsChange}
          />
          <StatCard
            label="Avg Messages / Chat"
            value={s.overview.avg_messages_per_conversation}
            subtext="messages per conversation"
          />
        </div>

        {/* Row 2: Trend Lines */}
        {s.trends.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Monthly Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={s.trends}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                    <RechartsTooltip />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="conversations"
                      stroke={TREND_COLORS.conversations}
                      strokeWidth={2}
                      name="Conversations"
                      dot={{ r: 4 }}
                    />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="unique_users"
                      stroke={TREND_COLORS.unique_users}
                      strokeWidth={2}
                      name="Unique Users"
                      dot={{ r: 4 }}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="avg_messages_per_conversation"
                      stroke={TREND_COLORS.avg_messages}
                      strokeWidth={2}
                      name="Avg Msgs/Chat"
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Row 3: Skill Usage (full width) */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Skill Usage</CardTitle>
          </CardHeader>
          <CardContent>
            {skillUsageData.length > 0 ? (
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={skillUsageData} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                    <RechartsTooltip />
                    <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]} name="Sessions" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground py-8 text-center">No skill usage data yet</div>
            )}
          </CardContent>
        </Card>

        {/* Row 4: Quality + Skills Breakdown — 3 columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>Quality Feedback</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-around">
                <QualityDonut data={s.quality} />
              </div>
              <div className="flex justify-center gap-4 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-green-500" /> Positive
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-500" /> Negative
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Skills by Execution Mode */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>By Execution Mode</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={executionModeData}
                      innerRadius={30}
                      outerRadius={55}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {executionModeData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="text-center text-xs text-muted-foreground">
                {s.skills.active} active / {s.skills.total} total
              </div>
            </CardContent>
          </Card>

          {/* Skills by Type */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle>By Skill Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={skillTypeData}
                      innerRadius={30}
                      outerRadius={55}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {skillTypeData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        <GuidedUseCasesSection
          stats={(stats as DashboardStats & { guided_use_cases?: GuidedUseCaseStats })?.guided_use_cases}
        />
          </TabsContent>
        </Tabs>
      </ContentContainer>
    </>
  );
}
